import { useState, useCallback } from 'react';
import { message } from 'antd';
import { getDetailedReevaluationPatients } from '../../../services/reservation';

import countrySettings from '../countrySettings.json';

const useDetailedReevaluation = ({ countryCode }) => {
  const [pagination, setPagination] = useState({
    inProgress: { page: 1, total: 0, perPage: 30 },
    completedDocuments: { page: 1, total: 0, perPage: 30 },
    finalized: { page: 1, total: 0, perPage: 30 },
  });

  const [advancedFilters, setAdvancedFilters] = useState({ page: 1 });

  const [sorting, setSorting] = useState({
    inProgress: countrySettings.reevaluationList.defaultSorting.inProgress[countryCode],
    completedDocuments: countrySettings.reevaluationList.defaultSorting.completedDocuments[countryCode],
    finalized: countrySettings.reevaluationList.defaultSorting.finalized[countryCode],
  });

  const [data, setData] = useState({
    inProgress: [],
    completedDocuments: [],
    finalized: [],
  });

  const [loading, setLoading] = useState({
    inProgress: false,
    completedDocuments: false,
    finalized: false,
    branchOffices: false,
  });

  const allSections = [
    {
      key: 'inProgress',
      enabled: true,
      type: 'inProgress',
      errorMessage: 'Error al cargar reevaluaciones en progreso'
    },
    {
      key: 'completedDocuments',
      enabled: false,
      type: 'completedDocuments',
      errorMessage: 'Error al cargar reevaluaciones con documentos completos'
    },
    {
      key: 'finalized',
      enabled: true,
      type: 'finalized',
      errorMessage: 'Error al cargar reevaluaciones finalizadas'
    },
  ];

  const setSectionLoading = (key, value) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  };

  const updatePagination = (key, paginationData) => {
    setPagination(prev => ({
      ...prev,
      [key]: {
        page: paginationData.currentPage,
        total: paginationData.total,
        perPage: paginationData.perPage,
        lastPage: paginationData.lastPage,
      },
    }));
  };

  const handlePageChange = (sectionKey, direction) => {
    setPagination(prev => {
      const currentPage = prev[sectionKey].page;
      const lastPage = prev[sectionKey].lastPage;

      let newPage = direction === 'next' ? currentPage + 1 : currentPage - 1;
      newPage = Math.max(1, Math.min(newPage, lastPage));

      const newFilters = { ...advancedFilters, page: newPage };
      setAdvancedFilters(newFilters);

      const section = allSections.find(s => s.key === sectionKey);
      fetchDetailedReevaluations(section, newFilters);

      return {
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          page: newPage,
        },
      };
    });
  };

  const handleOrderChange = (sectionKey, columnKey, direction) => {
    const section = allSections.find(s => s.key === sectionKey);

    setSorting(prev => ({
      ...prev,
      [sectionKey]: {
        orderBy: columnKey,
        orderDirection: direction,
      },
    }));

    const newFilters = {
      ...advancedFilters,
      orderBy: columnKey,
      orderDirection: direction,
      page: 1,
    };

    setAdvancedFilters(newFilters);
    fetchDetailedReevaluations(section, newFilters);
  };

  const fetchDetailedReevaluations = useCallback(
    async (section, params) => {
      if (!section.enabled) return;

      const baseParams = { ...params };
      const sort = sorting[section.key];

      const fetchParams = {
        ...baseParams,
        ...(sort.orderBy && { orderBy: sort.orderBy }),
        ...(sort.orderDirection && { orderDirection: sort.orderDirection }),
      };

      setSectionLoading(section.key, true);

      try {
        const response = await getDetailedReevaluationPatients({
          ...fetchParams,
          reevaluationType: section.type,
        });

        setData(prev => ({
          ...prev,
          [section.key]: response?.data ?? [],
        }));

        updatePagination(section.key, response.pagination);
      } catch (error) {
        console.error(error);
        message.error(section.errorMessage);
      } finally {
        setSectionLoading(section.key, false);
      }
    },
    [sorting]
  );

  const fetchAllSections = async (params) => {
    await Promise.all(
      allSections.map(section =>
        section.enabled ? fetchDetailedReevaluations(section, params) : null
      )
    );
  };

  return {
    inProgress: data.inProgress,
    completedDocuments: data.completedDocuments,
    finalized: data.finalized,
    filters: advancedFilters,
    setFilters: setAdvancedFilters,
    sorting,
    loading,
    setSectionLoading,
    pagination,
    handlePageChange,
    handleOrderChange,
    fetchAllSections,
    fetchDetailedReevaluations,
  };
};

export default useDetailedReevaluation;
